import { Color, Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import {
  FocusEventHandler,
  KeyboardEventHandler,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { MaskedInput } from 'rsuite';
import Calendar, { CalendarState } from 'rsuite/cjs/Calendar/Calendar';
import { useCalendarState } from 'rsuite/cjs/DatePicker/utils';
import defaultLocale from 'rsuite/locales/default';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import { ControlConfig, processDateValue } from 'dashboard/components/FormControlPilledInput/types';
import { DateValue, ValueType } from 'dashboard/models/graph-query';
import { Z_INDICES } from 'dashboard/styles/variables';
import { paragraphMixins } from 'design-system/mixins/text';
import { Elevation } from 'design-system/variables/effect';
import { Iso8601DateString } from 'lib/types';

// minimally extracted css required for rendering the calendar
import 'dashboard/components/FormControlPilledInput/rsuite-calendar.css';
import 'dashboard/components/FormControlPilledInput/rsuite-button.css';

interface EditableItemDatePickerProps {
  controlConfig: ControlConfig;
  deleteItem: () => void;
  deleteLastItem: () => void;
  inputRef: MutableRefObject<HTMLInputElement>;
  isLast: boolean;
  itemKey: string;
  value: DateValue;
  onValueChange: (value: DateValue) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  isCompact?: boolean;
}

const Container = styled('div')`
  display: inline-block;
`;

const EditableDatePicker = styled(MaskedInput).withConfig({
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  shouldForwardProp: (prop: string) => !['isCompact'].includes(prop),
})<{ isCompact: boolean }>`
  ${paragraphMixins.md};
  &::placeholder {
    font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
    color: ${({ theme }) => theme.color.text.tertiary};
  }

  background: none;
  border: none;

  display: inline;
  min-width: 40px;
  width: unset;
  ${({ isCompact }) => {
    if (isCompact) {
      return `
        padding: unset;
        height: 100%;
      `;
    }
    return `padding: ${Spacing[1]} 0;`;
  }};
`;

const PickerDateContainer = styled('div')`
  position: absolute;
  width: 360px;
  background: ${Color.PrimaryWhite};
  border-radius: ${BORDER_RADIUSES.rounded};
  box-shadow: ${Elevation.Mid};
  z-index: ${Z_INDICES.dropdown};
  color: black;
`;

const EditableItemDatePicker = ({
  deleteItem,
  deleteLastItem,
  inputRef,
  isLast,
  itemKey,
  onValueChange,
  value: initialValue,
  // Remove this flag once we've ramped GraphV2 to 100%
  isCompact = false,
}: EditableItemDatePickerProps) => {
  const [value, setValue] = useState<string>(
    initialValue?.moment
      ? DateTime.fromISO(initialValue.moment).toFormat('yyyy-MM-dd')
      : initialValue?.['raw-value'],
  );
  const [focused, setFocused] = useState<boolean>(false);
  const { calendarState, reset, openMonth } = useCalendarState();
  const internalRef = useRef<HTMLInputElement>();

  const handleChange = (value: string) => {
    setValue(value);
  };

  const commitValue = () => {
    const processedValue = processDateValue({
      ...initialValue,
      'raw-value': value,
      type: ValueType.Date,
    });

    onValueChange(processedValue);

    // TODO: See if we can remove this by changing 'defaultValue' and
    // making use of useEffect
    setValue('');
  };

  const handleKeyPress: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter' && value.trim() !== '') {
      e.preventDefault();
      commitValue();
    }
  };

  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Tab' && value.trim() !== '') {
      e.preventDefault();

      commitValue();
    }

    if (e.key === 'Backspace' && value.trim() === '') {
      if (isLast) {
        deleteLastItem();
      } else {
        deleteItem();
      }
    }
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);

    if (initialValue['raw-value'] !== value) {
      commitValue();
    }
  };

  useEffect(() => {
    internalRef.current?.setAttribute('size', JSON.stringify(value.length || 12));
  }, [value]);

  const handleControlDrivenDateChange = useCallback(
    (date: Date) => {
      setValue(DateTime.fromJSDate(date).startOf('day').toFormat('yyyy-MM-dd'));
      // Focus the input so the user can still type
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      internalRef.current.focus();
    },
    [setValue],
  );

  const handleToggleMonthDropdown = useCallback(() => {
    if (calendarState === CalendarState.DROP_MONTH) {
      reset();
    } else {
      openMonth();
    }
  }, [calendarState, openMonth, reset]);

  const passCalendarDate = (date: Iso8601DateString | '') => {
    // Assume we'll have to leverage the current time when we receive an empty string input
    let dateValue: DateTime = DateTime.local();
    // If the datetime is valid, we'll use it
    if (DateTime.fromISO(date).isValid) {
      dateValue = DateTime.fromISO(date);
    }
    return new Date(dateValue.year, dateValue.month - 1, dateValue.day);
  };

  return (
    <Container>
      <OutsideClickHandler onOutsideClick={handleBlur}>
        <EditableDatePicker
          mask={(value: string) => {
            if (value[0] === '{') {
              return false;
            } else {
              return [/\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/];
            }
          }}
          placeholder='YYYY-MM-DD'
          format='yyyy-MM-dd'
          guide={value[0] !== '{'}
          key={itemKey}
          inputRef={(input: HTMLInputElement) => {
            internalRef.current = input;
            inputRef.current = input;
          }}
          value={value}
          onFocus={handleFocus}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          isCompact={isCompact}
        />
        {focused && (!value || value[0] !== '{') && (
          <PickerDateContainer>
            <div>
              <Calendar
                calendarDate={passCalendarDate(value)}
                calendarState={calendarState}
                format='yyyy-MM-dd'
                inline
                locale={defaultLocale.Calendar}
                onChangePageDate={handleControlDrivenDateChange}
                onMoveBackward={handleControlDrivenDateChange}
                onMoveForward={handleControlDrivenDateChange}
                onSelect={handleControlDrivenDateChange}
                onToggleMonthDropdown={handleToggleMonthDropdown}
              />
            </div>
          </PickerDateContainer>
        )}
      </OutsideClickHandler>
    </Container>
  );
};

export default EditableItemDatePicker;
